import {InteractionEvent, Matrix, Rectangle, RenderTexture, Sprite, Texture} from 'pixi.js';
import {DraggableObject} from '../../../../../base/DraggableObject';
import {IPoint} from '../../../../../interfaces/primitives/IPoint';
import PixiUtil from '../../../../../../api/render/pixi/PixiUtil';
import {StackSelector} from '../../../../../controllers/StackSelector';
import {StackEvents} from './stack/StackEvents';
import {CardStackData, ICardStackData} from '../../../../../data/active_data/field_config/CardStackData';
import {TextureUrlLoader} from '../../../../../utils/TextureUrlLoader';
import MessageHelper from '../../../../../../../../helpers/MessageHelper';
import {CardStackDataEvent} from '../../../../../data/active_data/field_config/card_stack_data/CardStackDataEvent';
import {gsap, Sine} from 'gsap';
import {CardData} from '../../../../../data/active_data/field_config/card_stack_data/CardData';
import {ArrayWrapEvent} from '../../../../../data/active_data/base/static_config_data/array_wrap/ArrayWrapEvent';
import {ActiveData} from '../../../../../data/ActiveData';
import {HandController} from '../../../../../controllers/HandController';
import {CardView} from './card/CardView';
import {GameSceneEvents} from '../../../GameSceneEvents';
import {DynamicData} from '../../../../../data/DynamicData';
import {ColorOverlayFilter, OutlineFilter} from 'pixi-filters';
import {CardBetween} from './card/CardBetween';
import {Timeout} from '../../../../../utils/Timeout';
import {Conversion} from '../../../../../utils/Conversion';
import {PixiApplicationController} from '../../../../../controllers/PixiApplicationController';
import {StaticData} from '../../../../../data/StaticData';
import PixiMessenger from '../../../../../../api/render/pixi/PixiMessenger';
import {NewPixiSceneEvents} from '../../../../../events/NewPixiSceneEvents';
import EventApi from '../../../../../../../../api/EventsApi';
import {ActiveDataCommandsController} from '../../../../../controllers/ActiveDataCommandsController';
import {IRectangle} from '../../../../../interfaces/primitives/IRectangle';
import {CardSelector} from '../../../../../controllers/CardSelector';
import {SimiloButton} from './stack/SimiloButton';

export class Stack extends DraggableObject
{
    toDestroy: boolean = false;
    usedByPlayerFilter: OutlineFilter = new OutlineFilter(4, 0xff0000);
    tempOnTable: boolean = false;
    points: {leftTop: IPoint, rightTop: IPoint, leftBottom: IPoint, rightBottom: IPoint} = {
        leftTop: {x: 0, y: 0},
        rightTop: {x: 0, y: 0},
        leftBottom: {x: 0, y: 0},
        rightBottom: {x: 0, y: 0}
    };
    dragFlipped: boolean = false;
    public sourceStack: Stack | null = null;
    readonly viewHolder: Sprite;
    private readonly widthHalf: number;
    private readonly heightHalf: number;
    private readonly hei: number;
    private xTween: gsap.core.Tween | undefined;
    private yTween: gsap.core.Tween | undefined;
    private angleTween: gsap.core.Tween | undefined;
    private flipTween: gsap.core.Tween | undefined;
    private takeCardStackResolve: ((value: Stack) => void) | undefined;
    private onStackCreatedBindThis: (e: Event) => void;
    private viewFront: CardView | undefined;
    private viewBack: CardView | undefined;
    private highlightTimeout: number | undefined;
    private colorOverlayFilter: ColorOverlayFilter = new ColorOverlayFilter(0x555555);
    private alreadyFlipped: boolean = false;
    private between: CardBetween;
    private offsetYHalf: number;
    private moreThan1Card: boolean = false;
    private backedTexture: RenderTexture;
    private backedSprite: Sprite;
    private maxStackHeight: number = 30;
    private min: number = 0;
    private movingX: boolean = false;
    private movingY: boolean = false;
    private needProcessClearUsedByPlayer: boolean = false;
    private needProcessClearBlockedByPlayer: boolean = false;
    private onPlayersUpdatedBindThis: (e: Event) => void;
    private pointerIsDownWithShift: boolean = false;
    private dragStartAngle: number = 0;
    private similoButton: SimiloButton | undefined;

    //private pointsGraphics: Graphics = new Graphics();

    constructor(public activeData: CardStackData) {
        super();

        this.activeData.addListener(CardStackDataEvent.X_CHANGED, this.onXChanged, this);
        this.activeData.addListener(CardStackDataEvent.Y_CHANGED, this.onYChanged, this);
        this.activeData.addListener(CardStackDataEvent.ANGLE_CHANGED, this.onAngleChanged, this);
        this.activeData.addListener(CardStackDataEvent.LOCKED_CHANGED, this.onLockedChanged, this);
        this.activeData.addListener(CardStackDataEvent.USED_BY_PLAYER_CHANGED, this.onUsedByPlayerChanged, this);
        this.activeData.addListener(CardStackDataEvent.BLOCKED_BY_PLAYER_CHANGED, this.onBlockedByPlayerChanged, this);
        this.activeData.addListener(CardStackDataEvent.FLIPPED, this.onFlipped, this);
        this.activeData.addListener(CardStackDataEvent.SHUFFLED, this.onShuffled, this);
        this.activeData.cards.addListener(ArrayWrapEvent.ELEMENT_REMOVED, this.onCardRemoved, this);
        this.activeData.cards.addListener(ArrayWrapEvent.ELEMENT_ADDED, this.onCardAdded, this);
        this.activeData.invisibleFor.addListener(ArrayWrapEvent.ELEMENT_REMOVED, this.onInvisibleForChanged, this);
        this.activeData.invisibleFor.addListener(ArrayWrapEvent.ELEMENT_ADDED, this.onInvisibleForChanged, this);

        this.onPlayersUpdatedBindThis = this.onPlayersUpdated.bind(this);
        StaticData.isAdmin || addEventListener(NewPixiSceneEvents.PLAYERS_UPDATED, this.onPlayersUpdatedBindThis);

        this.onStackCreatedBindThis = this.onStackCreated.bind(this);
        if (this.activeData.masterLocked) {
            this.interactive = this.interactiveChildren = this.buttonMode = StaticData.isAdmin;
        }
        let frontCard: CardData = this.activeData.cards.getLast();
        this.widthHalf = Math.floor(frontCard.width / 2);
        this.hei = frontCard.height;
        this.heightHalf = Math.floor(this.hei / 2);
        this.offsetYHalf = this.min / 2;
        this.between = new CardBetween(this.min, frontCard.height);
        this.viewHolder = new Sprite();
        this.viewHolder.pivot.set(this.widthHalf, this.heightHalf);
        this.viewHolder.position.set(this.widthHalf, this.heightHalf);
        this.addChild(this.viewHolder);
        this.viewHolder.rotation = Conversion.degreesToRadians(this.activeData.angle);

        this.backedTexture = RenderTexture.create({
            width: frontCard.width + this.maxStackHeight * 2,
            height: frontCard.height + this.maxStackHeight * 2
        });
        this.backedSprite = new Sprite(this.backedTexture);
        this.addChild(this.backedSprite);
        this.backedSprite.pivot.set(this.widthHalf + this.maxStackHeight, this.heightHalf + this.maxStackHeight);
        this.backedSprite.position.set(this.widthHalf, this.heightHalf);

        if (this.activeData.usedByPlayer) {
            this.onUsedByPlayerChanged(this.activeData.usedByPlayer);
            if (this.activeData.usedByPlayer == StaticData.playerId && this.activeData.id != -1) {
                StackSelector.selectStack(this, false);
            }
        }

        this.x = this.activeData.x;
        this.y = this.activeData.y;

        this.onInvisibleForChanged();
    }

    private _flipCorner: number = 0;

    /**
     * Используется в твине
     */
    get flipCorner(): number {
        return this._flipCorner;
    }

    /**
     * Поворот в перспективе
     * @param corner - Значние от 0 до PI
     */
    set flipCorner(corner: number) {
        if (corner > Math.PI / 2 && !this.alreadyFlipped) {
            this.alreadyFlipped = true;
            if (this.moreThan1Card) {
                this.viewBack!.filters = [];
                this.viewFront!.filters = [this.colorOverlayFilter];
                this.viewHolder.swapChildren(this.viewBack!, this.viewFront!);
            }
        }
        this._flipCorner = corner;
        this.viewFront!.flipCorner = this._flipCorner;
        let xOffset: number = this.min * Math.sin(corner);
        if (this.moreThan1Card) {
            this.viewBack!.flipCorner = this._flipCorner;
            this.viewFront!.x = -xOffset;
            this.viewBack!.x = xOffset;
            this.between!.setVertices([
                -xOffset, 0,
                xOffset, 0,
                -xOffset, this.hei,
                xOffset, this.hei
            ]);
        }
    }

    private get moving(): boolean {
        return this.movingY || this.movingX;
    }

    /**
     * Перерисовка при изменении видимости
     */
    onInvisibleForChanged(): void {
        this.visible = !this.activeData.invisibleFor.interfaces.includes(StaticData.playerId) && this.activeData.onTable;
    }

    /**
     * Перерисовка при изменении кол-ва карт
     */
    async onCardRemoved(): Promise<void> {
        await this.initialize();
    }

    /**
     * Перерисовка при изменении кол-ва карт и показ тултипа
     */
    async onCardAdded(): Promise<void> {
        await this.initialize();
        this.dispatchTooltipOnEvent();
    }

    /**
     * Обработчик внутриигрового события FLIPPED
     */
    async onFlipped(): Promise<void> {
        this.viewHolder.visible = true;
        this.backedSprite.visible = false;
        this.flipTween && this.flipTween.pause();
        this.between!.visible = true;
        await new Promise(resolve => this.flipTween = gsap.to(this, {
            flipCorner: Math.PI,
            duration: .3,
            ease: Sine.easeInOut,
            onComplete: resolve
        }));
        await this.initialize();
        this.backedSprite.visible = true;
        this.viewHolder.visible = false;
    }

    /**
     * Обработчик внутриигрового события SHUFFLED
     */
    async onShuffled(): Promise<void> {
        let waitInterval: number = 50;
        this.interactive = this.interactiveChildren = false;
        await this.shake(waitInterval);
        await this.shake(waitInterval);
        await this.initialize();
        await this.shake(waitInterval);
        await this.shake(waitInterval);
        this.viewHolder.scale.set(1, 1);
        this.backedSprite.scale.set(1, 1);
        this.interactive = this.interactiveChildren = true;
    }

    /**
     * Установка цвета
     */
    async initialize(): Promise<void> {
        if (!this.activeData.cards.length) {
            return;
        }

        this.min = Math.min(this.activeData.cards.length, this.maxStackHeight);

        let frontCard: CardData = this.activeData.cards.getLast();
        let backCard: CardData = this.activeData.cards.getAt(0);
        let frontTexture: Texture | null = await TextureUrlLoader.load(backCard.flipped ? frontCard.backface_src : frontCard.face_src, frontCard.width, frontCard.height);
        let backTexture: Texture | null = await TextureUrlLoader.load(backCard.flipped ? backCard.face_src : backCard.backface_src, backCard.width, backCard.height);

        if (this.viewFront) {
            this.viewHolder.removeChild(this.viewFront);
            this.viewFront.destroy();
            this.viewFront = undefined;
        }

        if (this.viewBack) {
            this.viewHolder.removeChild(this.between!);
            this.viewHolder.removeChild(this.viewBack!);
            this.viewBack!.destroy();
            this.viewBack = undefined;
        }

        this.moreThan1Card = this.activeData.cards.length > 1;

        if (this.moreThan1Card) {
            this.viewBack = new CardView(frontCard, frontTexture!, backTexture!, this.activeData.id);
            this.viewBack.filters = [this.colorOverlayFilter];
            this.viewHolder.addChild(this.viewBack);
            this.viewHolder.addChild(this.between!);
            this.viewBack.interactive = this.viewBack.interactiveChildren = false;
            this.between.interactive = this.between.interactiveChildren = false;
            this.between!.x = this.widthHalf;
            this.between!.visible = false;
        }

        this.viewFront = new CardView(frontCard, frontTexture!, backTexture!, this.activeData.id);
        this.viewHolder.addChild(this.viewFront).interactiveChildren = false;

        this.alreadyFlipped = false;
        this.flipCorner = 0;

        this.draw();
        this.updatePoints();
    }

    /**
     * Применяем позиционирование
     * @param position - объект содержащий координаты применяемой позиции по осям
     */
    moveTo(position: IPoint): void {
        this.x = position.x;
        this.y = position.y;
        this.activeData.config!.x = position.x;
        this.activeData.config!.y = position.y;
        this.updatePoints();
    }

    /**
     * Применяем новые координаты мыши
     */
    tryMove(): void {
        if (this.activeData.locked) {
            return;
        }
        let x: number = this.movePosition.x - this.pointerStartOffset.x;
        let y: number = this.movePosition.y - this.pointerStartOffset.y;
        let deltaMove: IPoint = this.clampPos(x, y);
        let result: IPoint = {x: deltaMove.x - this.x, y: deltaMove.y - this.y};
        this.startMoveCoors.x += result.x;
        this.startMoveCoors.y += result.y;

        if (!this.dragged && (Math.abs(this.startMoveCoors.x) > StaticData.dragSilentArea || Math.abs(this.startMoveCoors.y) > StaticData.dragSilentArea)) {
            if (!StackSelector.selected(this)) {
                if (this.pointerIsDownWithShift) {
                    StackSelector.tryAddFilter(this, this.usedByPlayerFilter);
                } else {
                    StackSelector.tryRemoveFilters(StackSelector.stashedStacks);
                    StackSelector.stashedStacks.length && PixiMessenger.sendEnableCardMessage(StackSelector.stashedStacks.map(stack => stack.activeData.id));
                    StackSelector.unstash();
                }
                let highlighted: Stack | null = StackSelector.highlighted();
                StackSelector.selectStack(this, !this.pointerIsDownWithShift);
                EventApi.triggerEvent(EventApi.START_SOUND, {sound: 'select'});
                highlighted && StackSelector.highlight(highlighted);
            }
            this.dragged = true;
        }

        if (this.dragged) {
            StackSelector.moveSelectedOnBy(result);
        }
    }

    /**
     * Ограничение перемещения внутри области перетаскивания
     * @param x - желаемая координата по горизонтали
     * @param y - желаемая координата по вертикали
     * @returns объект содержащий возможные координаты, полученные из желаемых
     */
    clampPos(x: number, y: number): IPoint {
        let selectedStacksRectangle: Rectangle = StackSelector.getSelectedStacksRectangle();
        x = PixiUtil.clamp(this.x - selectedStacksRectangle.left, x, ActiveData.root.width - selectedStacksRectangle.right + this.x);
        y = PixiUtil.clamp(this.y - selectedStacksRectangle.top, y, ActiveData.root.height - selectedStacksRectangle.bottom + this.y);
        return {x, y};
    }

    /**
     * Обработчик события pointerover
     * @param e Событие взаимодейтсвия пользователя
     */
    onPointerOver(e: InteractionEvent): void {
        if (DynamicData.dragSomething &&
            !this.activeData.locked &&
            !StackSelector.selectedStacks.find(stack => stack.activeData.width != this.activeData.width || stack.activeData.height != this.activeData.height)
        ) {

            this.highlightTimeout = window.setTimeout(() => {
                StackSelector.highlight(this);
            }, 1000);
        }
        StackSelector.hoverStack(this);
        //e.stopPropagation();
        this.dispatchTooltipOnEvent();
    }

    /**
     * Обработчик события pointerout
     * @param e Событие взаимодейтсвия пользователя
     */
    onPointerOut(e: InteractionEvent): void {
        window.clearTimeout(this.highlightTimeout);
        StackSelector.unhighlight();
        StackSelector.unhoverStack();
        e.stopPropagation();
        this.dispatchTooltipOffEvent();
    }

    /**
     * Обработчик события pointerdown
     * @param e Событие взаимодейтсвия пользователя
     */
    async onPointerDown(e: InteractionEvent): Promise<void> {
        if (this.activeData.locked || this.activeData.masterLocked) {
            this.dispatchTooltipOnEvent();
            super.onPointerDown(e);
            return;
        }
        PixiMessenger.sendDisableCardMessage([this.activeData.id], true);
        this.dragFlipped = false;
        this.dragStartAngle = this.activeData.angle;
        CardSelector.unselectAll();
        CardSelector.unhighlight();

        this.dispatchTooltipOffEvent();
        this.pointerIsDownWithShift = e.data.originalEvent.shiftKey;
        //Если колода не выделена и в ней больше одной карты
        if (!StackSelector.selected(this) && this.activeData.cards.length > 1) {
            e.stopPropagation();

            //создаём колоду, состоящую из одной карты, взятой из текущей колоды
            let stack: Stack = await this.takeCardSilently();
            //меняем т.к. от сервера создаётся колода в x:1500, y:1500
            stack.x = this.x;
            stack.y = this.y;
            stack.sourceStack = this;
            stack.updateUseFilterColor(StaticData.playerId);
            dispatchEvent(new MessageEvent(StackEvents.ON_STACK_TAKE_CARD, {data: {stack}}));
            //Сохраняем выделенные в данный момент колоды и добавляем на них вируальное выделение
            if (!this.pointerIsDownWithShift) {
                StackSelector.stash();
                StackSelector.unselectAll(true);
                StackSelector.highlight(this);
                StackSelector.tryAddFilters(StackSelector.stashedStacks);
                StackSelector.tryAddFilters([stack]);
            }
            //пробрасываем ивент в новую колоду, чтобы она могла начать таскаться
            await stack.onPointerDown(e);
            stack.interactive = stack.interactiveChildren = false;
            //в исходной колоде больше не делаем ничего
            return;
        }

        //Начинаем таскать колоду
        super.onPointerDown(e);
        //Внутриигровой ивент начала взаимодействия с колодой
        dispatchEvent(new MessageEvent(StackEvents.ON_STACK_POINTER_DOWN, {data: {stack: this}}));
        e.stopPropagation();
    }

    /**
     * Обработчик события pointerup
     * @param e Событие взаимодейтсвия пользователя
     */
    async onPointerUp(e: InteractionEvent, right: boolean = false): Promise<void> {
        if (this.toDestroy) {
            return;
        }
        //запоминаем была ли колода перемещена, т.к. после super.onPointerUp(e), this.dragged будет false
        let dragged: boolean = this.dragged;
        super.onPointerUp(e);
        this.activeData.masterLocked || this.dispatchStackPointerUpEvent();
        let x: number = this.x;
        let y: number = this.y;
        let highlightedStack: Stack | null = StackSelector.highlighted();
        StackSelector.unhighlight();
        if (this.activeData.locked || this.activeData.masterLocked) {
            right && dispatchEvent(new MessageEvent(StackEvents.ON_STACK_RIGHT_UP, {
                data: {
                    stack: this,
                    event: e
                }
            }));
            return;
        }

        if (this.sourceStack) {
            //если есть родительская колода, возвращаем в неё единственную карту из этой колоды
            PixiMessenger.sendEnableCardMessage([this.sourceStack.activeData.id]);

            if (dragged) {
                let otherStacks: Stack[] = StackSelector.selectedStacks.filter(stack => stack.activeData.id != -1);
                if (highlightedStack == this.sourceStack) {
                    this.sourceStack.putStackSilently(this);
                    this.sourceStack.dispatchTooltipOnEvent();
                } else {
                    if (highlightedStack) {
                        this.sourceStack.putStackSilently(this, true, false);
                        this.putVirtualCardToHighLightedStack(highlightedStack, right);
                        this.sourceStack.dispatchTooltipOnEvent();
                    } else {
                        this.putVirtualCardNotToStack(x, y, right);
                    }
                }
                otherStacks.length && await this.stacksEndDrag(otherStacks, highlightedStack, false);
                StackSelector.unselectAll();
            } else {
                StackSelector.tryRemoveFilters([this]);
                this.sourceStack.putStackSilently(this);
                this.sourceStack.onPointerUp(e, right);
                this.toDestroy = true;
            }
            //
            this.sourceStack = null;
            this.toDestroy && this.destroy();
            return;
        }
        if (dragged) {
            await this.stacksEndDrag(StackSelector.selectedStacks, highlightedStack);
            StackSelector.unselectAll();
            EventApi.triggerEvent(EventApi.START_SOUND, {sound: 'itemMovedToTable'});
        } else {
            EventApi.triggerEvent(EventApi.START_SOUND, {sound: 'select'});
            StackSelector.tryRemoveFilters(StackSelector.stashedStacks);
            if (this.pointerIsDownWithShift) {
                if (StackSelector.stashedStacks.length) {
                    StackSelector.unstash();
                    StackSelector.tryAddFilters(StackSelector.selectedStacks);
                }
                StackSelector.toggleSelected(this);
            } else {
                StackSelector.unselectStashed();
                StackSelector.clearStash();
                StackSelector.selected(this) || StackSelector.selectStack(this, !right);
            }
            right && dispatchEvent(new MessageEvent(StackEvents.ON_STACK_RIGHT_UP, {data: {stack: this, event: e}}));
        }
    }

    /**
     * Обработчик события pointerupoutside
     * @param e Событие взаимодейтсвия пользователя
     */
    onPointerUpOutside(e: InteractionEvent): void {
        super.onPointerUpOutside(e);
        this.dispatchStackPointerUpEvent();
    }

    /**
     * Обработчик события onrightup
     * @param e Событие взаимодейтсвия пользователя
     */
    async onRightUp(e: InteractionEvent): Promise<void> {
        await this.onPointerUp(e, true);
        super.onRightUp(e);
    }

    /**
     * Добавляем колоду в текущую колоду
     * @param stacks добавляемая колода
     */
    async putStacks(stacks: Stack[]): Promise<void> {
        MessageHelper.sendChangeStacksMessage(MessageHelper.TYPE_MERGE_STACKS, {
            stacks: stacks.map(item => item.activeData.id),
            id: this.activeData.id
            //flip: false
        });
    }

    /**
     * Уничтожаем колоду
     */
    destroy(): void {
        this.activeData.removeListener(CardStackDataEvent.X_CHANGED, this.onXChanged, this);
        this.activeData.removeListener(CardStackDataEvent.Y_CHANGED, this.onYChanged, this);
        this.activeData.removeListener(CardStackDataEvent.ANGLE_CHANGED, this.onAngleChanged, this);
        this.activeData.removeListener(CardStackDataEvent.LOCKED_CHANGED, this.onLockedChanged, this);
        this.activeData.removeListener(CardStackDataEvent.USED_BY_PLAYER_CHANGED, this.onUsedByPlayerChanged, this);
        this.activeData.removeListener(CardStackDataEvent.BLOCKED_BY_PLAYER_CHANGED, this.onBlockedByPlayerChanged, this);
        this.activeData.removeListener(CardStackDataEvent.FLIPPED, this.onFlipped, this);
        this.activeData.removeListener(CardStackDataEvent.SHUFFLED, this.onShuffled, this);
        this.activeData.cards.removeListener(ArrayWrapEvent.ELEMENT_REMOVED, this.onCardRemoved, this);
        this.activeData.cards.removeListener(ArrayWrapEvent.ELEMENT_ADDED, this.onCardAdded, this);
        this.activeData.invisibleFor.removeListener(ArrayWrapEvent.ELEMENT_REMOVED, this.onInvisibleForChanged, this);
        this.activeData.invisibleFor.removeListener(ArrayWrapEvent.ELEMENT_ADDED, this.onInvisibleForChanged, this);

        this.removeChild(this.backedSprite);
        this.backedSprite.destroy();
        this.backedTexture.destroy();

        removeEventListener(StackEvents.STACK_CREATED, this.onStackCreatedBindThis);
        removeEventListener(NewPixiSceneEvents.PLAYERS_UPDATED, this.onPlayersUpdatedBindThis);

        StackSelector.unselectStack(this);

        delete this.onStackCreatedBindThis;
        delete this.onPlayersUpdatedBindThis;
        delete this.takeCardStackResolve;
        delete this.activeData;
        delete this.colorOverlayFilter;
        delete this.between;
        delete this.viewFront;
        delete this.viewBack;
        delete this.xTween;
        delete this.yTween;
        delete this.backedSprite;
        delete this.backedTexture;

        super.destroy();
    }

    /**
     * Обработчик события создания колоды на столе
     * @param e - внутриигровой ивент
     */
    onStackCreated(e: Event): void {
        removeEventListener(StackEvents.STACK_CREATED, this.onStackCreatedBindThis);
        let data: {stack: Stack} = (e as MessageEvent).data;
        let stack: Stack = data.stack;
        this.takeCardStackResolve && this.takeCardStackResolve(stack);
        this.takeCardStackResolve = undefined;
    }

    /**
     * Вызов тултипа
     */
    public dispatchTooltipOnEvent(): void {
        let cardsLength: number = this.activeData.cards.length;
        dispatchEvent(new MessageEvent(GameSceneEvents.TOOLTIP_ON, {
            data: {
                text: cardsLength > 1 ? cardsLength.toString() : '',
                locked: this.activeData.locked
            }
        }));
    }

    /**
     * Сохраняем выделение до конца анимации перемещения по столу
     */
    checkMovingComplete(): void {
        if (this.moving) {
            return;
        }
        this.updatePoints();
        this.needProcessClearBlockedByPlayer && this.processClearBlockedByPlayer();
        this.needProcessClearUsedByPlayer && this.processClearUsedByPlayer();
    }

    /**
     * Проверяем на ниличие угловых точек колоды внутри заданного прямоугольника (для выделения объектов прямоугогльником)
     * @param rect - прямоугольник внутри которого ищем угловые точк
     */
    hasPointInRect(rect: IRectangle): boolean {
        let rectRight: number = rect.x + rect.width;
        let rectBottom: number = rect.y + rect.height;
        let leftTopHit: boolean = this.points.leftTop.x > rect.x && this.points.leftTop.x < rectRight && this.points.leftTop.y > rect.y && this.points.leftTop.y < rectBottom;
        let rightTopHit: boolean = this.points.rightTop.x > rect.x && this.points.rightTop.x < rectRight && this.points.rightTop.y > rect.y && this.points.rightTop.y < rectBottom;
        let leftBottomHit: boolean = this.points.leftBottom.x > rect.x && this.points.leftBottom.x < rectRight && this.points.leftBottom.y > rect.y && this.points.leftBottom.y < rectBottom;
        let rightBottomHit: boolean = this.points.rightBottom.x > rect.x && this.points.rightBottom.x < rectRight && this.points.rightBottom.y > rect.y && this.points.rightBottom.y < rectBottom;
        return leftTopHit || rightTopHit || leftBottomHit || rightBottomHit;
    }

    /**
     * Обработчик внутриигрового события PLAYERS_UPDATED
     * @param e - событие
     */
    private onPlayersUpdated(e: Event): void {
        e;
        if (this.activeData.usedByPlayer) {
            this.updateUseFilterColor(this.activeData.usedByPlayer);
        } else {
            this.updateUseFilterColor(StaticData.playerId);
        }
    }

    /**
     * Рисуем толщину колоды с прибавлением по одной карте через каждые 4 карты
     */
    private draw(): void {
        let transform: Matrix = new Matrix();
        PixiApplicationController.app.renderer.render(new Sprite(), {
            renderTexture: this.backedTexture,
            clear: true,
            transform
        });

        for (let step: number = this.min - this.min % 4; step > -1; step -= 4) {
            transform.tx = step * Math.sin(this.viewHolder.rotation) + this.maxStackHeight;
            transform.ty = step * Math.cos(this.viewHolder.rotation) + this.maxStackHeight;
            PixiApplicationController.app.renderer.render(this.viewFront!, {
                renderTexture: this.backedTexture,
                clear: false,
                transform
            });
        }
        this.backedSprite.rotation = this.viewHolder.rotation;
    }

    /**
     * Период анимации перемешивания
     * @param waitInterval - временной интернвал между состояниями
     */
    private async shake(waitInterval: number): Promise<void> {
        this.backedSprite!.scale.set(1.05, .95);
        await Timeout.milliseconds(waitInterval);
        this.backedSprite!.scale.set(.95, 1.05);
        await Timeout.milliseconds(waitInterval);
    }

    /**
     * Сокрытие тултипа
     */
    private dispatchTooltipOffEvent(): void {
        dispatchEvent(new MessageEvent(GameSceneEvents.TOOLTIP_OFF, {data: {}}));
    }

    /**
     * Внутриигровой ивент окончания взаимодействия с колодой
     */
    private dispatchStackPointerUpEvent(): void {
        if (this.activeData.id == -1) {
            return;
        }
        dispatchEvent(new MessageEvent(StackEvents.ON_STACK_POINTER_UP, {data: {stack: this}}));
    }

    /**
     * Взять карту из колоды, фактически создаём колоду из одной карты
     * помещаем в неё первую или последнюю карту в зависимости от потого перевёрнута ли исходная
     * ожидаем события создания колоды на столе
     */
    private async takeCardSilently(): Promise<Stack> {
        addEventListener(StackEvents.STACK_CREATED, this.onStackCreatedBindThis);
        let config: ICardStackData = {
            angle: this.activeData.angle,
            blockedByPlayer: this.activeData.blockedByPlayer,
            cards: [this.activeData.cards.splice(this.activeData.cards.length - 1, 1)[0].config!],
            flippedFor: [],
            hiddenFor: [],
            invisibleFor: [],
            visibleFor: [],
            id: -1,//временная виртуальная колода, о которой не знает сервер
            locked: this.activeData.locked,
            masterLocked: this.activeData.masterLocked,
            tags: [],
            title: this.activeData.title,
            usedByPlayer: this.activeData.usedByPlayer,
            width: this.activeData.width,
            height: this.activeData.height,
            onTable: true,
            x: this.activeData.x,
            y: this.activeData.y,
            z: this.activeData.z
        };
        this.initialize();
        ActiveData.hideEvents();
        ActiveData.dispatchEventsPool();
        ActiveData.showEvents();
        ActiveData.root.card_stacks.push(config);
        ActiveData.dispatchEventsPool();

        return new Promise<Stack>(resolve => {
            this.takeCardStackResolve = resolve;
        });
    }

    /**
     * Обработчик изменения значения x в модели
     * @param x - новое значение x
     */
    private onXChanged(x: number): void {
        if (this.x == x) {
            return;
        }

        this.xTween && this.xTween.pause();
        this.movingX = true;
        this.xTween = gsap.to(this, {
            x,
            duration: .5,
            ease: Sine.easeInOut,
            onComplete: () => {
                this.movingX = false;
                this.checkMovingComplete();
            }
        });
    }

    /**
     * Обработчик изменения значения y в модели
     * @param y - новое значение y
     */
    private onYChanged(y: number): void {
        if (this.y == y) {
            return;
        }
        this.yTween && this.yTween.pause();
        this.movingY = true;
        this.yTween = gsap.to(this, {
            y,
            duration: .5,
            ease: Sine.easeInOut,
            onComplete: () => {
                this.movingY = false;
                this.checkMovingComplete();
            }
        });
    }

    /**
     * Обработчик изменения значения angle в модели
     * @param angle - новое значение angle
     */
    private onAngleChanged(angle: number): void {
        this.updatePoints();
        this.angleTween && this.angleTween.pause();
        this.angleTween = gsap.to(this.viewHolder, {
            rotation: Conversion.degreesToRadians(angle),
            duration: .25,
            ease: Sine.easeInOut,
            onUpdate: () => {
                this.draw();
            }
        });
    }

    /**
     * Обработчик изменения значения angle в модели
     * @param angle - новое значение angle
     */
    private onLockedChanged(angle: number): void {
        StackSelector.hovered() === this && this.dispatchTooltipOnEvent();
    }

    /**
     * Возвращаем таскаемую колоду в текущую
     * @param stack - таскаемая колодо с которой окончили взаимодейтвие
     * @param clearTableTempCard - уничтожаем ли временную колоду на столе (для избежания исчезания временной колоды до ответа от сервера)
     * @param putCardsEvent - объявляем ли об этом событии
     */
    private putStackSilently(stack: Stack, clearTableTempCard: boolean = true, putCardsEvent: boolean = true): void {
        this.activeData.cards.push(stack.activeData.cards.getAt(0).config!);
        putCardsEvent || ActiveData.hideEvents();
        ActiveData.dispatchEventsPool();
        putCardsEvent || ActiveData.showEvents();

        clearTableTempCard && ActiveDataCommandsController.removeTempStack(stack);
    }

    /**
     * Окончание таскания колод
     * @param stacks - массив колод, таскание которых окончено
     * @param highlighted - подсвеченная колода
     * @param sounds - проигрывать ли соотв. звук
     */
    private async stacksEndDrag(stacks: Stack[], highlighted: Stack | null, sounds: boolean = false): Promise<void> {
        //Если поднимаем кнопку мыши над подсвеченное колодой - тогда мержим их
        if (highlighted && !StackSelector.selected(highlighted)) {
            await highlighted.putStacks(stacks);
            sounds && EventApi.triggerEvent(EventApi.START_SOUND, {sound: 'addToPile'});
            this.dispatchTooltipOnEvent();
            return;
        }
        //Если поднимаем кнопку мыши над рукой - тогда все выделенные колоды кладём в руку
        if (HandController.handHighLighted || CardSelector.highlighted()) {
            HandController.moveStacksToHand(stacks);
            return;
        }
        //иначе просто отправляем команду о завершении перетаскивания на сервер
        MessageHelper.sendChangeStacksMessage(MessageHelper.TYPE_MOVE_ON_TABLE, {
            stacks: stacks.map(stack => {
                return {id: stack.activeData.id, x: stack.activeData.x, y: stack.activeData.y};
            }),
            flip: this.dragFlipped,
            delta: this.activeData.angle - this.dragStartAngle
        });
        this.dispatchTooltipOnEvent();
    }

    /**
     * Кладём снятую одиночную карту не в колоду
     * @param x - координата X в координатах стола
     * @param y- координата Y в координатах стола
     * @param right - флаг правой кнопки мыши
     */
    private putVirtualCardNotToStack(x: number, y: number, right: boolean): void {
        if (HandController.handHighLighted || CardSelector.highlighted()) {
            this.sourceStack!.putStackSilently(this, true, false);
            HandController.moveCardsToHand([this.sourceStack!.activeData.cards.getLast()]);
        } else {
            this.sourceStack!.putStackSilently(this, false, false);
            //обозначаемся как временная карта для уничтожения в момент появления реальной карты от сервера FST-1698
            this.tempOnTable = true;
            this.interactive = this.interactiveChildren = false;
            MessageHelper.sendChangeCardsMessage(MessageHelper.TYPE_MOVE_CARD_ON_TABLE, {
                x,
                y,
                id: this.sourceStack!.activeData.cards.getLast().id,
                flip: this.dragFlipped,
                delta: this.activeData.angle - this.dragStartAngle
            });
            right || StackSelector.unselectAll();
        }
    }

    /**
     * Кладём снятую одиночную карту в подсвеченную колоду
     * @param highlighted - подсвеченная колода
     * @param right - флаг правой кнопки мыши
     */
    private putVirtualCardToHighLightedStack(highlighted: Stack, right: boolean) {
        if (!StackSelector.selected(highlighted)) {
            EventApi.triggerEvent(EventApi.START_SOUND, {sound: 'addToPile'});
            MessageHelper.sendChangeCardsMessage(MessageHelper.TYPE_MOVE_SOLO_CARD_TO_STACK, {
                ids: [this.sourceStack!.activeData.cards.getLast().id],
                stackId: highlighted.activeData.id,
                flip: false
            });
        }
        right || StackSelector.unselectAll();
    }

    /**
     * Обработчик внутриигрового события USED_BY_PLAYER_CHANGED
     * @param playerId - идентификатор пользователя
     */
    private onUsedByPlayerChanged(playerId: string): void {
        if (playerId) {
            this.updateUseFilterColor(playerId);
            StackSelector.tryAddFilter(this, this.usedByPlayerFilter);
            return;
        }
        if (this.moving) {
            this.needProcessClearUsedByPlayer = true;
        } else {
            this.processClearUsedByPlayer();
        }
    }

    /**
     * Обработчик внутриигрового события BLOCKED_BY_PLAYER_CHANGED
     * @param playerId - идентификатор пользователя
     */
    private onBlockedByPlayerChanged(playerId: string): void {
        if (playerId.length) {
            if (playerId != StaticData.playerId) {
                this.interactive = this.interactiveChildren = false;
            }
            return;
        }
        if (this.moving) {
            this.needProcessClearBlockedByPlayer = true;
        } else {
            this.processClearBlockedByPlayer();
        }
    }

    /**
     * Отложенное снятие обводки с колоды после окончания анимации перемещения по столу
     */
    private processClearUsedByPlayer(): void {
        this.needProcessClearUsedByPlayer = false;
        StackSelector.tryRemoveFilter(this, this.usedByPlayerFilter);
    }

    /**
     * Отложенное включение интерактивности колоды после окончания анимации перемещения по столу
     */
    private processClearBlockedByPlayer() {
        this.needProcessClearBlockedByPlayer = false;
        this.interactive = this.interactiveChildren = true;
    }

    /**
     * Обновляем цвет обводки колоды
     * @param playerId - идентификатор взаимодействующего с ней пользователя
     */
    private updateUseFilterColor(playerId: string): void {
        if (!DynamicData.playersColorsMap.has(playerId)) {
            StackSelector.tryRemoveFilters([this]);
            return;
        }
        let color: string = DynamicData.playersColorsMap.get(playerId)!.split('#')[1];
        this.usedByPlayerFilter.color = parseInt('0x' + color);
    }

    /**
     * Обновляенм позиции угловых точек для возможности выделения колод прямоуггольником
     */
    private updatePoints() {
        let radians: number = Conversion.degreesToRadians(this.activeData.angle);
        let sin: number = Math.sin(radians);
        let cos: number = Math.cos(radians);
        let sinHeightHalf: number = sin * this.heightHalf;
        let sinWidthHalf: number = sin * this.widthHalf;
        let cosHeightHalf: number = cos * this.heightHalf;
        let cosWidthHalf: number = cos * this.widthHalf;
        let widthHalfMinusCosWidthHalf: number = this.widthHalf - cosWidthHalf;
        let widthHalfPlusCosWidthHalf: number = this.widthHalf + cosWidthHalf;
        let heightHalfMinusCosHeightHalf: number = this.heightHalf - cosHeightHalf;
        let heightHalfPlusCosHeightHalf: number = this.heightHalf + cosHeightHalf;
        let sinWidthHalfPlusActiveDataY: number = sinWidthHalf + this.activeData.y;
        let minusSinWidthHalfPlusActiveDataY: number = -sinWidthHalf + this.activeData.y;
        let sinHeighthHalfPlusActiveDataX: number = sinHeightHalf + this.activeData.x;
        let minusSinHeighthHalfPlusActiveDataX: number = -sinHeightHalf + this.activeData.x;

        this.points.leftTop.x = widthHalfMinusCosWidthHalf + sinHeighthHalfPlusActiveDataX;
        this.points.leftTop.y = heightHalfMinusCosHeightHalf + minusSinWidthHalfPlusActiveDataY;
        this.points.rightTop.x = widthHalfPlusCosWidthHalf + sinHeighthHalfPlusActiveDataX;
        this.points.rightTop.y = heightHalfMinusCosHeightHalf + sinWidthHalfPlusActiveDataY;
        this.points.leftBottom.x = widthHalfMinusCosWidthHalf + minusSinHeighthHalfPlusActiveDataX;
        this.points.leftBottom.y = heightHalfPlusCosHeightHalf + minusSinWidthHalfPlusActiveDataY;
        this.points.rightBottom.x = widthHalfPlusCosWidthHalf + minusSinHeighthHalfPlusActiveDataX;
        this.points.rightBottom.y = heightHalfPlusCosHeightHalf + sinWidthHalfPlusActiveDataY;
    }
}
